import React from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Plug, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const ConnectorPill = ({ name, active }) => (
  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs font-medium ${
    active ? 'border-green-300 bg-green-50 text-green-700' : 'border-slate-200 bg-slate-50 text-slate-400'
  }`}>
    <Plug className="h-3 w-3" />
    {name}
    {active
      ? <Badge className="bg-green-500 text-white text-xs py-0 px-1">ON</Badge>
      : <Badge variant="outline" className="text-xs py-0 px-1">OFF</Badge>
    }
  </div>
);

const MetricBox = ({ label, value, sub, highlight }) => (
  <div className={`rounded-xl border p-4 ${highlight ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'}`}>
    <p className="text-xs text-slate-500 uppercase tracking-wide font-medium mb-1">{label}</p>
    <p className={`text-3xl font-bold ${highlight ? 'text-green-700' : 'text-slate-900'}`}>{value ?? '—'}</p>
    {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
  </div>
);

const SocialMetric = ({ label, total, new7d }) => (
  <div className="rounded-lg border bg-white border-slate-200 p-3">
    <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{label}</p>
    <p className="text-2xl font-bold text-slate-900">{total ?? '—'}</p>
    {new7d !== null && new7d !== undefined ? (
      <p className={`text-xs mt-0.5 font-medium ${new7d > 0 ? 'text-green-600' : new7d < 0 ? 'text-red-500' : 'text-slate-400'}`}>
        {new7d > 0 ? `+${new7d}` : new7d} últimos 7 días
      </p>
    ) : null}
  </div>
);

function extractNew7dFromTimeline(timelineData) {
  try {
    const values = timelineData?.data?.[0]?.values;
    if (!values || values.length < 2) return null;
    const last7 = values.slice(-7);
    if (last7.length < 2) return null;
    return (last7[last7.length - 1]?.value ?? 0) - (last7[0]?.value ?? 0);
  } catch {
    return null;
  }
}

const MetricsDashboard = ({ metrics, connectorStatus, followersNew7d, onRefresh, toast }) => {
  const stripe = metrics?.stripe || {};
  const ga4 = metrics?.ga4 || {};
  const brevo = metrics?.brevo || {};
  const tiktok = metrics?.tiktok || {};
  const ig = metrics?.instagram || {};
  const fb = metrics?.facebook || {};
  const gsc = metrics?.search_console || {};
  const gmail = metrics?.gmail || {};

  const igNew7d = followersNew7d?.instagram ?? extractNew7dFromTimeline(ig.followers_timeline);
  const fbNew7d = followersNew7d?.facebook ?? null;
  const ttNew7d = followersNew7d?.tiktok ?? null;

  const conversionRate = (ga4.sessions > 0 && stripe.sales_count > 0)
    ? ((stripe.sales_count / ga4.sessions) * 100).toFixed(1)
    : null;

  const trafficSources = (ga4.traffic_sources || []).slice(0, 3)
    .map(s => `${s.source}: ${s.sessions}`)
    .join(' · ') || null;

  const testConnections = async () => {
    toast({ title: 'Probando conexiones...', description: '15-30 segundos' });
    try {
      const r = await axios.get(`${BACKEND_URL}/api/connectors/test`, { timeout: 90000 });
      const lines = Object.entries(r.data?.results || {})
        .map(([k, v]) => `${v?.ok ? '✓' : '✗'} ${k}: ${v?.ok ? 'OK' : (v?.reason || 'FAIL')}`)
        .join('\n');
      toast({ title: r.data?.all_ok ? 'Todo conectado ✓' : 'Hay fallos', description: lines, duration: 15000 });
      onRefresh();
    } catch (e) {
      toast({ title: 'Error', variant: 'destructive' });
    }
  };

  const gmailByAccount = gmail.by_account || [];
  const gmailTotal = gmail.unread_count ?? 0;
  const gmailTCB = gmailByAccount.find(a =>
    a.account === 'main' ||
    (a.email_addr || '').includes('tucodigobase') ||
    (a.account || '').includes('main')
  );
  const gmailPura = gmailByAccount.find(a =>
    (a.email_addr || '').includes('puragroupllc') ||
    (a.email_addr || '').includes('puragroup') ||
    (a.account || '').includes('pura')
  );

  return (
    <div className="space-y-4 mb-2">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Plug className="h-4 w-4" /> Conectores
            </CardTitle>
            <Button variant="outline" size="sm" onClick={testConnections}>
              <ShieldCheck className="h-3 w-3 mr-1" /> Probar conexiones
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <ConnectorPill name="Stripe" active={connectorStatus?.stripe} />
            <ConnectorPill name="GA4" active={connectorStatus?.ga4} />
            <ConnectorPill name="Search Console" active={connectorStatus?.search_console} />
            <ConnectorPill name="Brevo" active={connectorStatus?.brevo} />
            <ConnectorPill name="Instagram/FB" active={connectorStatus?.metricool} />
            <ConnectorPill name="TikTok" active={connectorStatus?.tiktok} />
            <ConnectorPill name="Gmail" active={connectorStatus?.gmail} />
            <ConnectorPill name="GitHub" active={connectorStatus?.github} />
            <ConnectorPill name="Clarity" active={connectorStatus?.clarity} />
            <ConnectorPill name="Vercel" active={connectorStatus?.vercel} />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricBox label="💰 Ventas semana" value={`${stripe.revenue_total ?? 0}€`} sub={`${stripe.sales_count ?? 0} ventas`} highlight={stripe.revenue_total > 0} />
        <MetricBox label="💰 Ventas hoy" value={`${stripe.today_revenue ?? 0}€`} sub={`${stripe.today_sales ?? 0} ventas`} highlight={stripe.today_revenue > 0} />
        <MetricBox
          label="🌐 Visitas semana"
          value={ga4.sessions ?? '—'}
          sub={[
            ga4.bounce_rate_pct ? `rebote ${ga4.bounce_rate_pct}%` : null,
            conversionRate ? `conv. ${conversionRate}%` : null,
          ].filter(Boolean).join(' · ') || null}
        />
        <MetricBox label="🌐 Tráfico origen" value=" " sub={trafficSources || 'Sin datos'} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SocialMetric label="📸 Instagram" total={ig.followers_count ?? 0} new7d={igNew7d} />
        <SocialMetric label="📘 Facebook" total={fb.followers_count ?? 0} new7d={fbNew7d} />
        <SocialMetric label="🎵 TikTok" total={tiktok.followers_count ?? 0} new7d={ttNew7d} />
        <SocialMetric label="📧 Brevo" total={brevo.contacts_total ?? 0} new7d={brevo.contacts_new_7d ?? null} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SocialMetric label="🔍 Clics Google (28d)" total={gsc.clicks ?? 0} new7d={undefined} />
        <SocialMetric label="📊 Posición Google" total={gsc.avg_position ? `#${parseFloat(gsc.avg_position).toFixed(1)}` : '—'} new7d={undefined} />
        <SocialMetric
          label="📬 Gmail TCB (48h)"
          total={gmailTCB ? gmailTCB.count : gmailTotal}
          new7d={null}
        />
        <SocialMetric
          label="📬 Gmail Pura (48h)"
          total={gmailPura ? gmailPura.count : (gmail.configured ? 0 : '—')}
          new7d={null}
        />
      </div>
    </div>
  );
};

export default MetricsDashboard;
